import { useEffect, useState } from 'react';
import { Card, ErrorMessage, Field, Loading, ModuleHeader, Tabs, Toggle, useAsync, useToast } from '../../components/ui';
import { IconSpark } from '../../components/icons';
import { DsfaBadge } from '../../components/DsfaBadge';
import { api } from '../../lib/api';

type AiProvider = 'anthropic' | 'openai' | 'openai_compatible';

const PROVIDER_LABELS: Record<AiProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  openai_compatible: 'Lokal / OpenAI-kompatibler Server',
};

/**
 * KI-Einstellungen (Kap. 5) – ein Konfigurationsbereich für alle KI-gestützten
 * Einsätze eines Mandanten.
 *
 * Jeder Einsatz (Chatbot, Rechnungsextraktion, künftige) hat eine eigene
 * Anbindung *und* einen eigenen Kontext-Text; eine Checkbox „Globale
 * Konfiguration verwenden“ lässt ihn stattdessen die gemeinsame Anbindung
 * des Betriebs nutzen. Der Kontext bleibt davon unberührt – Chatbot- und
 * Rechnungskontext haben inhaltlich nichts miteinander zu tun.
 *
 * Für welchen Mandanten bearbeitet wird, bestimmt – wie auf jeder anderen
 * mandantenabhängigen Administrationsseite – die Auswahl im Kopf
 * (`TenantSwitch`). SuperAdmins sehen zusätzlich eine Übersicht über alle
 * Mandanten (2.4), unabhängig von dieser Auswahl.
 */
export function AiSettingsPage() {
  const [tab, setTab] = useState<'global' | 'chatbot' | 'invoice-extraction'>('global');

  const globalConfig = useAsync(() => api.get('/api/catalogs/ai-global-config'), []);

  return (
    <div className="stack measure">
      <ModuleHeader
        icon={<IconSpark width={22} height={22} />}
        title="KI-Einstellungen"
        subtitle="Globale Anbindung und die einzelnen KI-gestützten Einsätze – wer sie bearbeiten darf, bestimmt das Recht „KI-Einstellungen verwalten“."
        pill="Konfiguration"
        tone="info"
      />

      {globalConfig.error ? (
        <div className="alert warning">
          Die KI-Einstellungen konnten nicht geladen werden.
        </div>
      ) : (
        <Card noBody>
          <div className="card-body tight" style={{ paddingBottom: 0 }}>
            <Tabs
              active={tab}
              onChange={setTab}
              tabs={[
                { key: 'global', label: 'Globale Konfiguration' },
                { key: 'chatbot', label: 'Chatbot' },
                { key: 'invoice-extraction', label: 'Rechnungsextraktion' },
              ]}
            />
          </div>
          <div className="card-body">
            {tab === 'global' && <GlobalConfigTab summary={globalConfig.data?.config} onSaved={globalConfig.reload} />}
            {tab === 'chatbot' && <ChatbotTab globalSummary={globalConfig.data?.config} />}
            {tab === 'invoice-extraction' && <InvoiceExtractionTab globalSummary={globalConfig.data?.config} />}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Wiederverwendete Anbindungsfelder                                   */
/* ------------------------------------------------------------------ */

function ConnectionFields({
  provider,
  setProvider,
  baseUrl,
  setBaseUrl,
  apiKey,
  setApiKey,
  hasApiKey,
  model,
  setModel,
}: {
  provider: AiProvider;
  setProvider: (value: AiProvider) => void;
  baseUrl: string;
  setBaseUrl: (value: string) => void;
  apiKey: string;
  setApiKey: (value: string) => void;
  hasApiKey: boolean;
  model: string;
  setModel: (value: string) => void;
}) {
  return (
    <div className="stack">
      <Field label="Anbieter">
        <select value={provider} onChange={(event) => setProvider(event.target.value as AiProvider)}>
          {(Object.keys(PROVIDER_LABELS) as AiProvider[]).map((option) => (
            <option key={option} value={option}>
              {PROVIDER_LABELS[option]}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="Server-Adresse (Basis-URL)"
        required={provider === 'openai_compatible'}
        hint={
          provider === 'openai_compatible'
            ? 'Pflicht für einen lokalen/selbst gehosteten Server, z. B. http://localhost:11434/v1 für Ollama.'
            : 'Optional – nur nötig, um die Standardadresse des Anbieters zu überschreiben (z. B. ein Proxy).'
        }
      >
        <input value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} placeholder="http://localhost:11434/v1" />
      </Field>

      <Field
        label={hasApiKey ? 'API-Schlüssel ersetzen' : 'API-Schlüssel hinterlegen'}
        hint="Bei einem lokalen Server ohne Zugriffsschutz kann dieses Feld leer bleiben."
      >
        <input
          type="password"
          autoComplete="off"
          placeholder={hasApiKey ? '•••••••• (unverändert lassen)' : 'sk-…'}
          value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
        />
      </Field>

      <Field label="Modell" hint="z. B. claude-sonnet-5, gpt-4o-mini oder ein lokaler Modellname wie llama3.1:8b.">
        <input value={model} onChange={(event) => setModel(event.target.value)} />
      </Field>
    </div>
  );
}

function GlobalUsageNote({ summary }: { summary: any }) {
  if (!summary?.has_api_key && !summary?.base_url) {
    return (
      <div className="alert warning" style={{ fontSize: 12.5 }}>
        Es ist noch keine globale KI-Konfiguration hinterlegt (Register „Globale Konfiguration“).
      </div>
    );
  }
  return (
    <div className="alert info" style={{ fontSize: 12.5 }}>
      Es wird die globale Konfiguration verwendet: {PROVIDER_LABELS[summary.provider as AiProvider] ?? summary.provider} ·{' '}
      {summary.model}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Globale Konfiguration                                               */
/* ------------------------------------------------------------------ */

function GlobalConfigTab({ summary, onSaved }: { summary: any; onSaved: () => void }) {
  const { notify } = useToast();
  const [provider, setProvider] = useState<AiProvider>('anthropic');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  useEffect(() => {
    if (!summary) return;
    setProvider(summary.provider);
    setBaseUrl(summary.base_url ?? '');
    setModel(summary.model);
  }, [summary]);

  if (!summary) return <Loading />;
  const hasApiKey = !!summary.has_api_key;

  const save = async () => {
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      await api.put('/api/catalogs/ai-global-config', {
        provider,
        baseUrl: baseUrl.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
        model,
      });
      setApiKey('');
      onSaved();
      notify('Globale KI-Konfiguration gespeichert.', 'success');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      setTestResult(await api.post('/api/catalogs/ai-global-config/test'));
    } catch (caught) {
      setTestResult({ ok: false, error: caught instanceof Error ? caught.message : 'Prüfung fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <p className="small muted">
        Eine gemeinsame KI-Anbindung für den Betrieb. Sie wirkt nur, wenn ein Einsatz sie über die Checkbox
        „Globale Konfiguration verwenden“ ausdrücklich nutzt – einzeln bleibt jeder Einsatz weiterhin frei
        konfigurierbar.
      </p>
      <ErrorMessage error={error} />
      <ConnectionFields
        provider={provider}
        setProvider={setProvider}
        baseUrl={baseUrl}
        setBaseUrl={setBaseUrl}
        apiKey={apiKey}
        setApiKey={setApiKey}
        hasApiKey={hasApiKey}
        model={model}
        setModel={setModel}
      />
      {testResult && (
        <div className={`alert ${testResult.ok ? 'success' : 'error'}`} style={{ fontSize: 12.5 }}>
          {testResult.ok ? 'Verbindung erfolgreich.' : testResult.error}
        </div>
      )}
      <div className="row between">
        <button className="btn sm" disabled={busy} onClick={test}>
          Verbindung prüfen
        </button>
        <button className="btn primary" onClick={save} disabled={busy || !model.trim()}>
          {busy ? 'Wird gespeichert …' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Chatbot                                                              */
/* ------------------------------------------------------------------ */

function ChatbotTab({ globalSummary }: { globalSummary: any }) {
  const { notify } = useToast();
  const data = useAsync(() => api.get('/api/catalogs/ai-chatbot-config'), []);
  const [isActive, setIsActive] = useState(false);
  const [useGlobal, setUseGlobal] = useState(false);
  const [provider, setProvider] = useState<AiProvider>('anthropic');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [systemContext, setSystemContext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  useEffect(() => {
    const config = data.data?.config;
    if (!config) return;
    setIsActive(!!config.is_active);
    setUseGlobal(!!config.use_global);
    setProvider(config.provider);
    setBaseUrl(config.base_url ?? '');
    setModel(config.model);
    setSystemContext(config.system_context ?? '');
  }, [data.data]);

  if (data.loading) return <Loading />;
  const hasApiKey = !!data.data?.config?.has_api_key;

  const save = async () => {
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      await api.put('/api/catalogs/ai-chatbot-config', {
        isActive,
        useGlobal,
        provider,
        baseUrl: baseUrl.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
        model,
        systemContext,
      });
      setApiKey('');
      data.reload();
      notify('KI-Chatbot gespeichert.', 'success');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      setTestResult(await api.post('/api/catalogs/ai-chatbot-config/test'));
    } catch (caught) {
      setTestResult({ ok: false, error: caught instanceof Error ? caught.message : 'Prüfung fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <p className="small muted">
        Eine eigene Meldeseite, auf der ein Sprachmodell die Meldung im Gespräch aufnimmt – erreichbar über dasselbe
        Objekt-Token wie die Portalseite, nur unter <code className="mono small">/ki-chat/&lt;Objekt-Token&gt;</code>{' '}
        statt <code className="mono small">/melden/&lt;Objekt-Token&gt;</code>. Titel, Einleitung und
        benutzerdefinierte Felder werden von der Portalseite übernommen.
      </p>

      <ErrorMessage error={error} />

      <Toggle checked={isActive} onChange={setIsActive} label={<>KI-Chatbot-Portal aktiv <DsfaBadge id="ai.chatbot" /></>} />
      <Toggle checked={useGlobal} onChange={setUseGlobal} label="Globale Konfiguration verwenden" />

      {useGlobal ? (
        <GlobalUsageNote summary={globalSummary} />
      ) : (
        <ConnectionFields
          provider={provider}
          setProvider={setProvider}
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          apiKey={apiKey}
          setApiKey={setApiKey}
          hasApiKey={hasApiKey}
          model={model}
          setModel={setModel}
        />
      )}

      {testResult && (
        <div className={`alert ${testResult.ok ? 'success' : 'error'}`} style={{ fontSize: 12.5 }}>
          {testResult.ok ? 'Verbindung erfolgreich.' : testResult.error}
        </div>
      )}
      <div className="row end">
        <button className="btn sm" disabled={busy} onClick={test}>
          Verbindung prüfen
        </button>
      </div>

      <Field
        label="Kontext / Standardinformationen"
        hint="Wird bei jeder Unterhaltung als Zusatzwissen mitgegeben – z. B. Öffnungszeiten, hausspezifische Hinweise, typische Ansprechpartner. Eigenständig für diesen Einsatz, unabhängig von der gewählten Anbindung."
      >
        <textarea
          rows={8}
          value={systemContext}
          onChange={(event) => setSystemContext(event.target.value)}
          placeholder="z. B.: Der Hausmeisterservice ist werktags von 7 bis 16 Uhr erreichbar. Notfälle außerhalb dieser Zeit bitte zusätzlich telefonisch melden."
        />
      </Field>

      <div className="row end">
        <button className="btn primary" onClick={save} disabled={busy || !model.trim()}>
          {busy ? 'Wird gespeichert …' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rechnungsextraktion                                                 */
/* ------------------------------------------------------------------ */

function InvoiceExtractionTab({ globalSummary }: { globalSummary: any }) {
  const { notify } = useToast();
  const data = useAsync(() => api.get('/api/catalogs/ai-invoice-extraction-config'), []);
  const [isActive, setIsActive] = useState(false);
  const [useGlobal, setUseGlobal] = useState(true);
  const [provider, setProvider] = useState<AiProvider>('anthropic');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [systemContext, setSystemContext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  useEffect(() => {
    const config = data.data?.config;
    if (!config) return;
    setIsActive(!!config.is_active);
    setUseGlobal(!!config.use_global);
    setProvider(config.provider);
    setBaseUrl(config.base_url ?? '');
    setModel(config.model);
    setSystemContext(config.system_context ?? '');
  }, [data.data]);

  if (data.loading) return <Loading />;
  const hasApiKey = !!data.data?.config?.has_api_key;

  const save = async () => {
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      await api.put('/api/catalogs/ai-invoice-extraction-config', {
        isActive,
        useGlobal,
        provider,
        baseUrl: baseUrl.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
        model,
        systemContext,
      });
      setApiKey('');
      data.reload();
      notify('Rechnungsextraktion gespeichert.', 'success');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      setTestResult(await api.post('/api/catalogs/ai-invoice-extraction-config/test'));
    } catch (caught) {
      setTestResult({ ok: false, error: caught instanceof Error ? caught.message : 'Prüfung fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <p className="small muted">
        Beim Erfassen einer Eingangsrechnung lässt sich eine hochgeladene PDF automatisch auslesen (Material &amp;
        Lagerwirtschaft → Eingangsrechnungen). Ist dieser Einsatz aktiv, kann dort zusätzlich zur einfachen
        Texterkennung eine KI-gestützte Erkennung gewählt werden, die auch Positionszeilen liefert.
      </p>

      <ErrorMessage error={error} />

      <Toggle checked={isActive} onChange={setIsActive} label={<>KI-gestützte Rechnungsextraktion aktiv <DsfaBadge id="ai.invoice" /></>} />
      <Toggle checked={useGlobal} onChange={setUseGlobal} label="Globale Konfiguration verwenden" />

      {useGlobal ? (
        <GlobalUsageNote summary={globalSummary} />
      ) : (
        <ConnectionFields
          provider={provider}
          setProvider={setProvider}
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          apiKey={apiKey}
          setApiKey={setApiKey}
          hasApiKey={hasApiKey}
          model={model}
          setModel={setModel}
        />
      )}

      {testResult && (
        <div className={`alert ${testResult.ok ? 'success' : 'error'}`} style={{ fontSize: 12.5 }}>
          {testResult.ok ? 'Verbindung erfolgreich.' : testResult.error}
        </div>
      )}
      <div className="row end">
        <button className="btn sm" disabled={busy} onClick={test}>
          Verbindung prüfen
        </button>
      </div>

      <Field
        label="Kontext / Standardinformationen"
        hint="Hinweise zur Rechnungserkennung des Betriebs, z. B. typische Lieferanten, Besonderheiten im Rechnungsformat oder Skonto-Regelungen. Eigenständig für diesen Einsatz, unabhängig von der gewählten Anbindung."
      >
        <textarea
          rows={6}
          value={systemContext}
          onChange={(event) => setSystemContext(event.target.value)}
          placeholder="z. B.: Rechnungen der GrünWerk Landschaftsbau GmbH weisen 2 % Skonto als eigene Position aus, die nicht als Rechnungsposition zu erfassen ist."
        />
      </Field>

      <div className="row end">
        <button className="btn primary" onClick={save} disabled={busy || !model.trim()}>
          {busy ? 'Wird gespeichert …' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}
